// Feature gating helpers — single API used by sidebar, route guards, and
// LockedModulePage. Always import from here; never inline module access logic.
import { getPlan, PLANS_LIST, hasFeature } from "@/lib/plansConfig";
import { getModule, MODULES_CATALOG } from "@/config/modulesConfig";

/**
 * Resolve the canonical plan object for a tenant. Accepts either:
 *   - tenant.plan_id   (backend DB id, e.g. "restaurant_business")
 *   - the tier id      (e.g. "professional")
 */
export function getTenantPlan(tenant) {
  if (!tenant) return null;
  return getPlan(tenant.plan_id) || getPlan(tenant.plan?.id) || null;
}

export function getPlanModules(planIdOrTier) {
  return getPlan(planIdOrTier)?.modules || [];
}

/**
 * Returns true when the plan grants access to the given sidebar module id.
 * Pulls the `requiredModule` from MODULES_CATALOG and delegates to hasFeature.
 *
 *   hasModuleAccess("professional", "ai_controller") → true
 *   hasModuleAccess("starter", "ai_controller")      → false
 *   hasModuleAccess("starter", "dashboard")          → true  (requiredModule = null)
 */
export function hasModuleAccess(planIdOrTier, sidebarModuleId) {
  const m = getModule(sidebarModuleId);
  if (!m) return false;
  if (m.requiredModule == null) return true; // no gating
  return hasFeature(planIdOrTier, m.requiredModule);
}

/**
 * Smallest paying plan that grants access to the module — used to suggest the
 * right upgrade target on LockedModulePage.
 *
 *   getUpgradeTarget("ai_controller")     → "professional"
 *   getUpgradeTarget("business_analytics") → "enterprise"
 *   getUpgradeTarget("dashboard")          → null (always available)
 */
export function getUpgradeTarget(sidebarModuleId) {
  const m = getModule(sidebarModuleId);
  if (!m) return null;
  if (m.requiredModule == null) return null;
  // Sort plans by monthly price ascending and pick the cheapest that includes it.
  const sorted = [...PLANS_LIST]
    .filter((p) => p.target_segment !== "hotel" && p.target_segment !== "combo")
    .sort((a, b) => (a.price_monthly_eur || 0) - (b.price_monthly_eur || 0));
  return (
    sorted.find((p) => p.modules.includes(m.requiredModule))?.id || null
  );
}

/**
 * Status of a module for the given plan. Used to decide sidebar styling.
 *
 *   "active"      → included in the current plan, normal link
 *   "upgrade"     → not included, available in a higher non-enterprise plan
 *   "enterprise"  → enterprise-only
 *   "unavailable" → no plan includes it (shouldn't happen — defensive)
 */
export function getModuleStatus(planIdOrTier, sidebarModuleId) {
  const m = getModule(sidebarModuleId);
  if (!m) return "unavailable";
  if (hasModuleAccess(planIdOrTier, sidebarModuleId)) return "active";
  if (m.enterpriseOnly) return "enterprise";
  const target = getUpgradeTarget(sidebarModuleId);
  if (!target) return "unavailable";
  const targetPlan = getPlan(target);
  if (targetPlan?.enterprise) return "enterprise";
  return "upgrade";
}

/**
 * Locked reason metadata (for LockedModulePage rendering).
 * Returns { reason, requiredPlanId, requiredPlanName, requiredPlanPrice }.
 */
export function getLockedReason(planIdOrTier, sidebarModuleId) {
  const status = getModuleStatus(planIdOrTier, sidebarModuleId);
  if (status === "active") return null;
  const target = getUpgradeTarget(sidebarModuleId);
  const targetPlan = target ? getPlan(target) : null;
  return {
    status,
    reason:
      status === "enterprise"
        ? "Funzionalità disponibile nel piano Enterprise."
        : "Funzionalità inclusa in un piano superiore.",
    requiredPlanId: targetPlan?.id || null,
    requiredPlanName: targetPlan?.name || null,
    requiredPlanPrice: targetPlan?.price_monthly_eur || null,
    requiredPlanCustom: targetPlan?.custom_pricing || false,
  };
}

/**
 * Filter sidebar modules by category, role, viewport. Used by Sidebar.jsx.
 *   role:   user role string ("staff_member" / "viewer" → restrict)
 *   plan:   tenant plan id
 *   mobile: when true, hide modules with mobileEnabled=false
 */
export function visibleSidebarModules({ plan, role, mobile = false } = {}) {
  const STAFF_BLOCKED_CATEGORIES = new Set([
    "ai_controller",
    "intelligence",
    "growth",
  ]);
  return Object.values(MODULES_CATALOG)
    .filter((m) => m.visibleInSidebar)
    .filter((m) => (mobile ? m.mobileEnabled : true))
    .filter((m) => {
      // Staff/viewer non vedono moduli avanzati di analisi.
      if (
        (role === "staff_member" || role === "viewer") &&
        STAFF_BLOCKED_CATEGORIES.has(m.category)
      ) {
        return false;
      }
      return true;
    })
    .map((m) => ({
      ...m,
      status: getModuleStatus(plan, m.id),
      lockedReason: getLockedReason(plan, m.id),
    }));
}
